import React, { useState, useEffect, useRef } from 'react';
import {
    Plus, X, FileUp, Image as ImageIcon, Layout, Settings, Save,
    Trash2, GripVertical, PlusCircle, BarChart2, CheckSquare,
    ChevronRight, ChevronLeft, Loader2, Monitor, Users, Edit2,
    Upload, Eye, AlertCircle, BookOpen
} from 'lucide-react';
import { slidesService } from '../../services/slidesService';
import { firestoreService } from '../../services/firestoreService';
import PdfSlide from './PdfSlide';

const SlidesManager = ({ bookId, bookData, isOpen, onClose }) => {
    const fileInputRef = useRef(null);

    // State
    const [decks, setDecks] = useState([]);
    const [selectedDeck, setSelectedDeck] = useState(null);
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const [editingDeckId, setEditingDeckId] = useState(null);
    const [error, setError] = useState(null);
    const [keepPdf, setKeepPdf] = useState(true);
    const [deckPdfUrl, setDeckPdfUrl] = useState(null);

    // Interaction modal state
    const [interactionModal, setInteractionModal] = useState(null);
    const [interactionData, setInteractionData] = useState({
        title: '',
        options: ['', ''],
        kind: 'poll',
        correctAnswer: null,
        allowMultiple: false
    });

    // Load decks on mount
    useEffect(() => {
        if (isOpen) {
            loadDecks();
        }
    }, [bookId, isOpen]);

    // Load slides when selected deck changes
    useEffect(() => {
        if (selectedDeck) {
            loadSlides(selectedDeck.id);
        } else {
            setSlides([]);
        }
    }, [selectedDeck]);

    useEffect(() => {
        let cancelled = false;

        const loadDeckPdfUrl = async () => {
            if (selectedDeck?.renderMode === 'pdf') {
                if (selectedDeck?.pdfStoragePath) {
                    try {
                        const url = await slidesService.getDownloadUrlFromStoragePath(selectedDeck.pdfStoragePath);
                        if (!cancelled) setDeckPdfUrl(url);
                    } catch (err) {
                        console.error('Failed to load deck PDF URL:', err);
                        if (!cancelled) setDeckPdfUrl(null);
                    }
                    return;
                }
                if (selectedDeck?.pdfUrl) {
                    setDeckPdfUrl(selectedDeck.pdfUrl);
                    return;
                }
            }
            setDeckPdfUrl(null);
        };

        loadDeckPdfUrl();
        return () => {
            cancelled = true;
        };
    }, [selectedDeck]);

    const loadDecks = async () => {
        setLoading(true);
        setError(null);
        try {
            const decksData = await slidesService.getSlideDecks(bookId);
            setDecks(decksData);
            if (decksData.length > 0) {
                const refreshed = selectedDeck
                    ? decksData.find(deck => deck.id === selectedDeck.id)
                    : decksData[0];
                setSelectedDeck(refreshed || null);
            } else {
                setSelectedDeck(null);
            }
        } catch (err) {
            console.error('Failed to load decks:', err);
            setError('Failed to load decks');
        } finally {
            setLoading(false);
        }
    };

    const loadSlides = async (deckId) => {
        try {
            const slidesData = await slidesService.getSlides(bookId, deckId);
            setSlides(slidesData);
            setActiveSlideIndex(0);
        } catch (err) {
            console.error('Failed to load slides:', err);
            setError('Failed to load slides');
        }
    };

    const handleCreateDeck = async () => {
        try {
            const newDeck = await slidesService.createSlideDeck(bookId, {
                title: `Deck ${decks.length + 1}`
            });
            await loadDecks();
            setSelectedDeck(newDeck);
        } catch (err) {
            console.error('Failed to create deck:', err);
            setError('Failed to create deck');
        }
    };

    const handleRenameDeck = async (deckId, title) => {
        try {
            await slidesService.updateSlideDeck(bookId, deckId, { title });
            setEditingDeckId(null);
            await loadDecks();
        } catch (err) {
            console.error('Failed to rename deck:', err);
            setError('Failed to rename deck');
        }
    };

    const handleDeleteDeck = async (deckId) => {
        if (!window.confirm('Delete this deck and all its slides?')) return;
        try {
            await slidesService.deleteSlideDeck(bookId, deckId);
            if (selectedDeck?.id === deckId) {
                setSelectedDeck(decks.find(d => d.id !== deckId) || null);
            }
            await loadDecks();
        } catch (err) {
            console.error('Failed to delete deck:', err);
            setError('Failed to delete deck');
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !selectedDeck) return;

        setUploading(true);
        setError(null);
        setUploadProgress({ current: 0, total: 1, percentage: 0 });

        try {
            await slidesService.uploadSlideFile(
                bookId,
                selectedDeck.id,
                file,
                (progress) => {
                setUploadProgress(progress);
                },
                { keepPdf }
            );
            await loadSlides(selectedDeck.id);
            await loadDecks();
            setUploadProgress(null);
        } catch (err) {
            console.error('Upload failed:', err);
            setError('Upload failed: ' + err.message);
        } finally {
            setUploading(false);
            setUploadProgress(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveSlide = async (slideId) => {
        if (!window.confirm('Delete this slide?')) return;
        try {
            await slidesService.deleteSlide(bookId, selectedDeck.id, slideId);
            await loadSlides(selectedDeck.id);
            await loadDecks();
        } catch (err) {
            console.error('Failed to delete slide:', err);
            setError('Failed to delete slide');
        }
    };

    // Interaction handlers
    const openInteractionModal = () => {
        setInteractionModal('choice');
        setInteractionData({
            title: 'New Question',
            options: ['', ''],
            kind: 'poll',
            correctAnswer: null,
            allowMultiple: false
        });
    };

    const addOption = () => {
        setInteractionData(prev => ({
            ...prev,
            options: [...prev.options, '']
        }));
    };

    const removeOption = (index) => {
        if (interactionData.options.length <= 2) return;
        setInteractionData(prev => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index),
            correctAnswer: prev.correctAnswer === index ? null :
                prev.correctAnswer > index ? prev.correctAnswer - 1 : prev.correctAnswer
        }));
    };

    const updateOption = (index, value) => {
        setInteractionData(prev => ({
            ...prev,
            options: prev.options.map((opt, i) => i === index ? value : opt)
        }));
    };

    const saveInteraction = async () => {
        if (!selectedDeck || !slides[activeSlideIndex]) return;
        if (!bookData?.slidesSettings?.allowOfflineInteractions) {
            setError('Offline interactions are disabled. Enable them in Slides Settings.');
            return;
        }

        const slide = slides[activeSlideIndex];
        const interaction = {
            id: Date.now().toString(),
            type: 'choice',
            kind: interactionData.kind,
            title: interactionData.title,
            options: interactionData.options.filter(o => o.trim()),
            correctAnswer: interactionData.kind === 'mcq' && !interactionData.allowMultiple
                ? interactionData.correctAnswer
                : null,
            allowMultiple: interactionData.allowMultiple,
            createdAt: new Date().toISOString(),
            responses: []
        };

        try {
            // Update the slide with the new interaction
            const updatedInteractions = [...(slide.interactions || []), interaction];
            await slidesService.updateSlide(bookId, selectedDeck.id, slide.id, {
                interactions: updatedInteractions
            });
            await loadSlides(selectedDeck.id);
            setInteractionModal(null);
        } catch (err) {
            console.error('Failed to save interaction:', err);
            setError('Failed to save interaction');
        }
    };

    const removeInteraction = async (slideId, interactionId) => {
        if (!window.confirm('Remove this interaction?')) return;

        const slide = slides.find(s => s.id === slideId);
        if (!slide) return;

        try {
            const updatedInteractions = (slide.interactions || []).filter(i => i.id !== interactionId);
            await slidesService.updateSlide(bookId, selectedDeck.id, slideId, {
                interactions: updatedInteractions
            });
            await loadSlides(selectedDeck.id);
        } catch (err) {
            console.error('Failed to remove interaction:', err);
            setError('Failed to remove interaction');
        }
    };

    if (!isOpen) return null;

    const currentSlide = slides[activeSlideIndex];

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                            <Monitor className="h-6 w-6" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Slides Manager</h2>
                            <p className="text-sm text-white/70">{bookData?.botName || 'Manage your presentations'}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-100 rounded">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Upload Progress */}
                {uploadProgress && (
                    <div className="mx-4 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-blue-900">
                                {uploadProgress.label ? uploadProgress.label : (uploadProgress.current && uploadProgress.total
                                    ? `Uploading: Page ${uploadProgress.current} of ${uploadProgress.total}`
                                    : 'Processing...')}
                            </span>
                            <span className="text-sm text-blue-700">{uploadProgress.percentage || 0}%</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress.percentage || 0}%` }}
                            />
                        </div>
                    </div>
                )}

                <div className="flex-1 flex overflow-hidden">
                    {/* Decks Sidebar */}
                    <div className="w-64 border-r bg-gray-50 flex flex-col">
                        <div className="p-4 border-b bg-white flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">Decks</h3>
                            <button
                                onClick={handleCreateDeck}
                                className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>

                        {loading ? (
                            <div className="flex-1 flex items-center justify-center">
                                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto p-2 space-y-2">
                                {decks.map(deck => (
                                    <div
                                        key={deck.id}
                                        className={`p-3 rounded-lg cursor-pointer transition-colors ${selectedDeck?.id === deck.id
                                                ? 'bg-indigo-50 border-2 border-indigo-500'
                                                : 'bg-white hover:bg-gray-100 border border-gray-200'
                                            }`}
                                    >
                                        {editingDeckId === deck.id ? (
                                            <div className="space-y-2">
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    defaultValue={deck.title}
                                                    onBlur={(e) => handleRenameDeck(deck.id, e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleRenameDeck(deck.id, e.target.value);
                                                        if (e.key === 'Escape') setEditingDeckId(null);
                                                    }}
                                                    className="w-full px-2 py-1 border rounded text-sm"
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <div onClick={() => setSelectedDeck(deck)}>
                                                    <h3 className="font-medium text-sm text-gray-900">{deck.title}</h3>
                                                    <p className="text-xs text-gray-500">{deck.slideCount || 0} slides</p>
                                                </div>
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setEditingDeckId(deck.id); }}
                                                        className="p-1 text-indigo-600 hover:bg-indigo-100 rounded"
                                                    >
                                                        <Edit2 className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteDeck(deck.id); }}
                                                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}

                                {decks.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                        <p className="text-sm">No decks yet</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 flex flex-col overflow-hidden bg-gray-100">
                        {selectedDeck ? (
                            <>
                                {/* Toolbar */}
                                <div className="flex justify-between items-center p-4 bg-white border-b">
                                    <h2 className="text-lg font-semibold text-gray-900">{selectedDeck.title}</h2>
                                    <div className="flex gap-3 items-center">
                                        <label className="flex items-center gap-2 text-xs text-gray-600">
                                            <input
                                                type="checkbox"
                                                checked={keepPdf}
                                                onChange={(e) => setKeepPdf(e.target.checked)}
                                                className="w-4 h-4 text-indigo-600 rounded"
                                            />
                                            Keep PDF as single file
                                        </label>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".pdf,image/*"
                                            onChange={handleFileUpload}
                                            className="hidden"
                                        />
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                                        >
                                            {uploading ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Uploading...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="w-4 h-4" />
                                                    Upload PDF/Images
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Content Grid */}
                                <div className="flex-1 flex overflow-hidden">
                                    {/* Slide Thumbnails */}
                                    <div className="w-48 border-r bg-white overflow-y-auto p-3 space-y-3">
                                        {slides.map((slide, index) => (
                                            <div
                                                key={slide.id}
                                                onClick={() => setActiveSlideIndex(index)}
                                                className={`relative group rounded-lg overflow-hidden cursor-pointer transition-all ${activeSlideIndex === index
                                                        ? 'ring-2 ring-indigo-500 ring-offset-2'
                                                        : 'hover:ring-2 hover:ring-gray-300'
                                                    }`}
                                            >
                                                <div className="aspect-video bg-gray-100 flex items-center justify-center">
                                                    {slide.type === 'pdf' && deckPdfUrl ? (
                                                        <PdfSlide
                                                            pdfUrl={deckPdfUrl}
                                                            pageNumber={slide.pageNumber || index + 1}
                                                            scale={0.25}
                                                            className="w-full h-full"
                                                        />
                                                    ) : (
                                                        <img
                                                            src={slide.url}
                                                            alt={`Slide ${index + 1}`}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    )}
                                                </div>

                                                {/* Slide Number */}
                                                <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                                                    {index + 1}
                                                </div>

                                                {/* Interaction indicators */}
                                                {slide.interactions?.length > 0 && (
                                                    <div className="absolute top-1 right-1 flex gap-0.5">
                                                        {slide.interactions.some(i => i.type === 'poll' || (i.type === 'choice' && i.kind === 'poll')) && (
                                                            <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                                                <BarChart2 className="w-2.5 h-2.5 text-white" />
                                                            </div>
                                                        )}
                                                        {slide.interactions.some(i => i.type === 'mcq' || (i.type === 'choice' && i.kind === 'mcq')) && (
                                                            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                                                <CheckSquare className="w-2.5 h-2.5 text-white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Delete button */}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleRemoveSlide(slide.id); }}
                                                    className="absolute top-1 left-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}

                                        {slides.length === 0 && (
                                            <div className="text-center py-8 text-gray-400">
                                                <Layout className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                                <p className="text-xs">No slides</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Main Slide View */}
                                    <div className="flex-1 p-6 overflow-y-auto">
                                        {slides.length > 0 ? (
                                            <div className="space-y-6">
                                                {/* Slide Preview */}
                                                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                                                    <div className="aspect-video bg-black relative group flex items-center justify-center">
                                                        {currentSlide?.type === 'pdf' && deckPdfUrl ? (
                                                            <PdfSlide
                                                                pdfUrl={deckPdfUrl}
                                                                pageNumber={currentSlide?.pageNumber || activeSlideIndex + 1}
                                                                scale={1.25}
                                                                className="w-full h-full"
                                                            />
                                                        ) : (
                                                            <img
                                                                src={currentSlide?.url}
                                                                className="w-full h-full object-contain"
                                                                alt="Current Slide"
                                                            />
                                                        )}

                                                        {/* Navigation Arrows */}
                                                        <button
                                                            onClick={() => setActiveSlideIndex(Math.max(0, activeSlideIndex - 1))}
                                                            disabled={activeSlideIndex === 0}
                                                            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 disabled:opacity-0 transition-all hover:bg-black/70"
                                                        >
                                                            <ChevronLeft className="w-6 h-6" />
                                                        </button>
                                                        <button
                                                            onClick={() => setActiveSlideIndex(Math.min(slides.length - 1, activeSlideIndex + 1))}
                                                            disabled={activeSlideIndex === slides.length - 1}
                                                            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 disabled:opacity-0 transition-all hover:bg-black/70"
                                                        >
                                                            <ChevronRight className="w-6 h-6" />
                                                        </button>

                                                        {/* Slide Counter */}
                                                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full font-bold">
                                                            {activeSlideIndex + 1} / {slides.length}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Add Interactions Section */}
                                                <div className="bg-white rounded-xl p-6 shadow-lg">
                                                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                                                        <PlusCircle className="w-4 h-4 text-indigo-600" />
                                                        Add Interactions to Slide {activeSlideIndex + 1}
                                                    </h3>

                                                    <div className="grid grid-cols-1 gap-4">
                                                        <button
                                                            onClick={openInteractionModal}
                                                            className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-indigo-50 to-slate-50 rounded-xl border-2 border-indigo-200 hover:border-indigo-400 transition-all group"
                                                        >
                                                            <div className="p-3 bg-indigo-500 rounded-xl text-white group-hover:scale-110 transition-transform">
                                                                <BarChart2 className="w-6 h-6" />
                                                            </div>
                                                            <div className="text-center">
                                                                <span className="font-bold text-gray-900">Choice</span>
                                                                <p className="text-xs text-gray-500 mt-1">Poll or MCQ with single/multiple selection</p>
                                                            </div>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Existing Interactions */}
                                                {currentSlide?.interactions?.length > 0 && (
                                                    <div className="bg-white rounded-xl p-6 shadow-lg">
                                                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">
                                                            Slide Interactions ({currentSlide.interactions.length})
                                                        </h3>
                                                        <div className="space-y-3">
                                                            {currentSlide.interactions.map((interaction, idx) => (
                                                                <div key={interaction.id || idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                                                                    <div className="flex items-center gap-3">
                                                                        {(() => {
                                                                            const kind = interaction.kind || interaction.type;
                                                                            return (
                                                                                <>
                                                                                    <div className={`p-2 rounded-lg ${kind === 'poll' ? 'bg-blue-100 text-blue-600' :
                                                                                        kind === 'mcq' ? 'bg-green-100 text-green-600' :
                                                                                            'bg-purple-100 text-purple-600'
                                                                                        }`}>
                                                                                        {kind === 'poll' && <BarChart2 className="w-4 h-4" />}
                                                                                        {kind === 'mcq' && <CheckSquare className="w-4 h-4" />}
                                                                                    </div>
                                                                                    <div>
                                                                                        <p className="font-medium text-gray-900">{interaction.title}</p>
                                                                                        <p className="text-xs text-gray-500 capitalize">{kind}</p>
                                                                                    </div>
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                    <button
                                                                        onClick={() => removeInteraction(currentSlide.id, interaction.id)}
                                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex-1 flex flex-col items-center justify-center py-20">
                                                <Layout className="w-20 h-20 text-gray-200 mb-4" />
                                                <h3 className="text-lg font-bold text-gray-600">No Slides Yet</h3>
                                                <p className="text-gray-500 text-sm mt-2 max-w-xs text-center">
                                                    Upload a PDF or images to add slides to this deck.
                                                </p>
                                                <button
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="mt-6 flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg transition-all"
                                                >
                                                    <Upload className="w-5 h-5" />
                                                    Upload Files
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center">
                                <Monitor className="w-24 h-24 text-gray-200 mb-4" />
                                <h3 className="text-xl font-bold text-gray-700">Select a Deck</h3>
                                <p className="text-gray-500 mt-2 max-w-xs text-center">
                                    Create a new deck or select one from the sidebar to start managing slides.
                                </p>
                                <button
                                    onClick={handleCreateDeck}
                                    className="mt-6 flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg transition-all"
                                >
                                    <Plus className="w-5 h-5" />
                                    Create New Deck
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Interaction Creation Modal */}
            {interactionModal && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
                        <div className={`p-6 text-white ${interactionData.kind === 'poll'
                                ? 'bg-gradient-to-r from-blue-600 to-blue-700'
                                : 'bg-gradient-to-r from-green-600 to-green-700'
                            }`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {interactionData.kind === 'poll' && <BarChart2 className="w-6 h-6" />}
                                    {interactionData.kind === 'mcq' && <CheckSquare className="w-6 h-6" />}
                                    <h3 className="text-xl font-bold">
                                        {interactionData.kind === 'poll' ? 'Create Poll' : 'Create MCQ'}
                                    </h3>
                                </div>
                                <button onClick={() => setInteractionModal(null)} className="p-2 hover:bg-white/20 rounded-full">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Label As</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setInteractionData(prev => ({ ...prev, kind: 'poll', correctAnswer: null }))}
                                        className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${interactionData.kind === 'poll'
                                                ? 'border-blue-600 bg-blue-50 text-blue-600'
                                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                            }`}
                                    >
                                        Poll
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setInteractionData(prev => ({ ...prev, kind: 'mcq' }))}
                                        className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${interactionData.kind === 'mcq'
                                                ? 'border-green-600 bg-green-50 text-green-600'
                                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                            }`}
                                    >
                                        MCQ
                                    </button>
                                </div>
                            </div>

                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Question/Title</label>
                                <input
                                    type="text"
                                    value={interactionData.title}
                                    onChange={(e) => setInteractionData(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Enter your question..."
                                />
                            </div>

                            {/* Options */}
                            <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Options {interactionData.kind === 'mcq' && !interactionData.allowMultiple && (
                                            <span className="text-gray-400 font-normal">(click to mark correct answer)</span>
                                        )}
                                    </label>
                                    <div className="space-y-2">
                                        {interactionData.options.map((option, idx) => (
                                            <div key={idx} className="flex items-center gap-2">
                                                {interactionData.kind === 'mcq' && !interactionData.allowMultiple && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setInteractionData(prev => ({ ...prev, correctAnswer: idx }))}
                                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${interactionData.correctAnswer === idx
                                                                ? 'bg-green-500 border-green-500 text-white'
                                                                : 'border-gray-300 hover:border-green-400'
                                                            }`}
                                                    >
                                                        {interactionData.correctAnswer === idx && '✓'}
                                                    </button>
                                                )}
                                                <input
                                                    type="text"
                                                    value={option}
                                                    onChange={(e) => updateOption(idx, e.target.value)}
                                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                    placeholder={`Option ${idx + 1}`}
                                                />
                                                {interactionData.options.length > 2 && (
                                                    <button
                                                        onClick={() => removeOption(idx)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        onClick={addOption}
                                        className="mt-3 flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Option
                                    </button>
                            </div>

                            {/* Allow Multiple */}
                            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={interactionData.allowMultiple}
                                    onChange={(e) => setInteractionData(prev => ({ ...prev, allowMultiple: e.target.checked, correctAnswer: null }))}
                                    className="w-5 h-5 text-indigo-600 rounded"
                                />
                                <div>
                                    <span className="font-medium text-gray-900">Allow multiple selections</span>
                                    <p className="text-xs text-gray-500">Participants can select more than one option</p>
                                </div>
                            </label>
                        </div>

                        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={() => setInteractionModal(null)}
                                className="px-6 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveInteraction}
                                disabled={!interactionData.title.trim() || (interactionData.kind === 'mcq' && !interactionData.allowMultiple && interactionData.correctAnswer === null)}
                                className={`px-6 py-2.5 text-white font-bold rounded-xl transition-all disabled:opacity-50 ${interactionData.kind === 'poll'
                                        ? 'bg-blue-600 hover:bg-blue-700'
                                        : 'bg-green-600 hover:bg-green-700'
                                    }`}
                            >
                                Add to Slide
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SlidesManager;
